import { useEffect, useState } from "react";
import { motion, useScroll, useSpring, useReducedMotion } from "framer-motion";

/**
 * Editorial reading-progress hairline.
 *
 * A 2px primary-tinted line that fills along the very top of the viewport as
 * the visitor scrolls the page. Common premium-publication detail (NYT Mag,
 * The Atlantic, Stripe Press) that quietly signals "you are a reader, this
 * is a long thoughtful page."
 *
 * Implementation notes:
 *  - Uses `useScroll()` page-wide progress + `useSpring` for buttery damping.
 *  - Hidden until the user has scrolled past the very top so it doesn't
 *    flicker on initial paint.
 *  - Hidden entirely under `prefers-reduced-motion` (no animated indicator).
 *  - Pure transform animation → off the main thread, no layout cost.
 *  - `aria-hidden` because it's a visual flourish; not navigationally useful.
 */
const ReadingProgress = () => {
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 26,
    mass: 0.4,
    restDelta: 0.001,
  });

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (reduced) return null;

  return (
    <motion.div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-[2px] z-[60] origin-left bg-gradient-to-r from-primary via-primary to-primary/60 pointer-events-none"
      style={{
        scaleX,
        opacity: visible ? 0.85 : 0,
        transition: "opacity 400ms ease-out",
      }}
    />
  );
};

export default ReadingProgress;
